// components/CRM/modales/TransicionEtapaModal.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Alert, CircularProgress,
  Stepper, Step, StepLabel
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { WORKFLOW_CONFIG, transicionarEtapa } from '../../../services/crmWorkflowService';

// Importar formularios dinámicamente
import ContactoInicialForm from '../EtapaWorkflow/ContactoInicialForm';
import NecesidadValidadaForm from '../EtapaWorkflow/NecesidadValidadaForm';
import PropuestaBuilder from '../EtapaWorkflow/PropuestaBuilder';
import NegociacionForm from '../EtapaWorkflow/NegociacionForm';
import CierreGanadoForm from '../EtapaWorkflow/CierreGanadoForm';
import CierrePerdidoForm from '../EtapaWorkflow/CierrePerdidoForm';
import DemoForm from '../EtapaWorkflow/DemoForm';

const FORMULARIOS = {
  ContactoInicialForm,
  NecesidadValidadaForm,
  PropuestaBuilder,
  NegociacionForm,
  CierreGanadoForm,
  CierrePerdidoForm,
  DemoForm
};

const TransicionEtapaModal = ({ 
  open, 
  onClose, 
  oportunidad, 
  etapaDestino, 
  onTransicionCompleta 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transicionExitosa, setTransicionExitosa] = useState(false);

  useEffect(() => {
    if (open) {
      setError(null);
      setTransicionExitosa(false);
    }
  }, [open, etapaDestino]);

  if (!oportunidad || !etapaDestino) return null;

  const configOrigen = WORKFLOW_CONFIG[oportunidad.etapa];
  const configDestino = WORKFLOW_CONFIG[etapaDestino];
  const nombreFormulario = getNombreFormulario(etapaDestino);
  const FormularioComponent = FORMULARIOS[nombreFormulario];

  function getNombreFormulario(etapa) {
    const mapa = {
      'contacto_inicial': 'ContactoInicialForm',
      'necesidad_validada': 'NecesidadValidadaForm',
      'demo': 'DemoForm',
      'propuesta_enviada': 'PropuestaBuilder',
      'negociacion': 'NegociacionForm',
      'cerrado_ganado': 'CierreGanadoForm',
      'cerrado_perdido': 'CierrePerdidoForm'
    };
    return mapa[etapa];
  }

const handleGuardadoFormulario = async (datosForm) => {
    try {
      setLoading(true);
      setError(null);

      // 1. Preparamos los datos finales manteniendo la inmutabilidad
      let datosFinales = { ...datosForm };
      
      // 2. 👑 APLICAMOS LA JERARQUÍA:
      // Si la etapa destino es negociación, el monto_final del slider 
      // debe suplantar al valor_estimado de la oportunidad.
      if (etapaDestino === 'negociacion' && datosForm.negociacion?.monto_final) {
        console.log("🎯 Suplantando valor_estimado con el monto negociado:", datosForm.negociacion.monto_final);
        datosFinales.valor_estimado = datosForm.negociacion.monto_final;
      }

      // 3. Enviamos los datos normalizados al servicio (usamos datosFinales, no datosForm)
      await transicionarEtapa(oportunidad.id, etapaDestino, datosFinales);

      // ✅ Marcar transición como exitosa
      setTransicionExitosa(true);
      setLoading(false);

    } catch (err) {
      console.error('Error en transición:', err);
      setError(err.response?.data?.error || err.message || 'Error al procesar la transición');
      setLoading(false);
    }
  };
  const getColorEtapa = (etapa) => WORKFLOW_CONFIG[etapa]?.color || '#6b7280';

  return (
    <Dialog 
      open={open} 
      onClose={!loading ? onClose : undefined} 
      maxWidth="lg" 
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ 
        bgcolor: getColorEtapa(etapaDestino), 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box>
          <Typography variant="h6">
            {configDestino?.icono} {configDestino?.nombre}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            {oportunidad.prospecto?.nombre} • Transición desde: {configOrigen?.nombre}
          </Typography>
        </Box>
        {!loading && (
          <Button onClick={onClose} sx={{ color: 'white', minWidth: 'auto' }}>
            <CloseIcon />
          </Button>
        )}
      </DialogTitle>
      
      <DialogContent dividers sx={{ minHeight: 500, p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography>Procesando transición...</Typography>
          </Box>
        )}
        
        {!loading && FormularioComponent && (
          <FormularioComponent
            oportunidad={oportunidad}
            onGuardado={handleGuardadoFormulario}
            transicionExitosa={transicionExitosa}
            onTransicionCompleta={() => {
              // Cuando el formulario llama a onTransicionCompleta
              // cerramos el modal y recargamos el pipeline
              if (onTransicionCompleta) {
                onTransicionCompleta();
              }
              onClose();
            }}
          />
        )}
        
        {!loading && !FormularioComponent && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert severity="info">
              No se requiere formulario adicional para esta transición.
            </Alert>
            <Button
              variant="contained"
              onClick={() => handleGuardadoFormulario({})}
              sx={{ mt: 2 }}
            >
              Confirmar Transición
            </Button>
          </Box>
        )}
      </DialogContent>
      
      {!loading && (
        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
          <Button onClick={onClose} color="inherit">
            Cancelar
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default TransicionEtapaModal;